import { useQuery, useMutation } from '@tanstack/react-query';
import { apiService } from './apiService';

/**
 * Fetch all items/assets
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-indexed)
 * @param {number} params.perPage - Number of items per page
 * @param {string} params.search - Search text (optional)
 * @param {string} params.category - Category filter (optional)
 * @param {string} params.status - Status filter (optional)
 * @returns {Promise<Object>} - Items data
 */
const fetchAllItems = async ({ page = 1, perPage = 10, search, category, status } = {}) => {
  let endpoint = `/items?page=${page}&per_page=${perPage}`;
  
  if (search) {
    endpoint += `&search=${encodeURIComponent(search)}`;
  }
  if (category) {
    endpoint += `&category=${encodeURIComponent(category)}`;
  }
  if (status) {
    endpoint += `&status=${encodeURIComponent(status)}`;
  }
  
  return apiService.get(endpoint, {}, 'v1');
};

/**
 * Fetch item/asset by ID
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} - Item data
 */
const fetchItemById = async (itemId) => {
  if (!itemId) return null;
  const endpoint = `/items/${itemId}`;
  return apiService.get(endpoint, {}, 'v1');
};

/**
 * Fetch item images by item ID
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} - Item images data
 */
const fetchItemImages = async (itemId) => {
  if (!itemId) return null;
  const endpoint = `/items/${itemId}/images`;
  return apiService.get(endpoint, {}, 'v1');
};

/**
 * Fetch item variants by item ID
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} - Item variants data
 */
const fetchItemVariants = async (itemId) => {
  if (!itemId) return null;
  const endpoint = `/items/${itemId}/variants`;
  return apiService.get(endpoint, {}, 'v1');
};

/**
 * Fetch item evaluations by item ID
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} - Item evaluations data
 */
const fetchItemEvaluations = async (itemId) => {
  if (!itemId) return null;
  const endpoint = `/items/${itemId}/evaluations`;
  return apiService.get(endpoint, {}, 'v1');
};

/**
 * Create a new item/asset
 * @param {Object} itemData - Item data to create
 * @returns {Promise<Object>} - Created item data
 */
const createItem = async (itemData) => {
  const endpoint = '/items';
  return apiService.post(endpoint, itemData, {}, 'v1');
};

/**
 * Update an existing item/asset
 * @param {string} itemId - Item ID
 * @param {Object} itemData - Item data to update
 * @returns {Promise<Object>} - Updated item data
 */
const updateItem = async (itemId, itemData) => {
  const endpoint = `/items/${itemId}`;
  return apiService.put(endpoint, itemData, {}, 'v1');
};

/**
 * Delete an item/asset
 * @param {string} itemId - Item ID
 * @returns {Promise<Object>} - Delete response
 */
const deleteItem = async (itemId) => {
  const endpoint = `/items/${itemId}`;
  return apiService.delete(endpoint, {}, 'v1');
};

// React Query Hooks

/**
 * Hook to fetch all items/assets with pagination and filtering
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (1-indexed)
 * @param {number} params.perPage - Number of items per page
 * @param {string} params.search - Search text (optional)
 * @param {string} params.category - Category filter (optional)
 * @param {string} params.status - Status filter (optional)
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useAllItems = (params = {}, options = {}) => {
  const { page = 1, perPage = 10, search, category, status } = params;
  
  return useQuery({
    queryKey: ['items', 'all', page, perPage, search, category, status],
    queryFn: () => fetchAllItems({ page, perPage, search, category, status }),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
};

/**
 * Hook to fetch item/asset by ID
 * @param {string} itemId - Item ID
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useItemById = (itemId, options = {}) => {
  return useQuery({
    queryKey: ['items', 'id', itemId],
    queryFn: () => fetchItemById(itemId),
    enabled: !!itemId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Hook to fetch item images by item ID
 * @param {string} itemId - Item ID
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useItemImages = (itemId, options = {}) => {
  return useQuery({
    queryKey: ['items', 'images', itemId],
    queryFn: () => fetchItemImages(itemId),
    enabled: !!itemId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
};

/**
 * Hook to fetch item variants by item ID
 * @param {string} itemId - Item ID
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useItemVariants = (itemId, options = {}) => {
  return useQuery({
    queryKey: ['items', 'variants', itemId],
    queryFn: () => fetchItemVariants(itemId),
    enabled: !!itemId,
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
};

/**
 * Hook to fetch item evaluations by item ID
 * @param {string} itemId - Item ID
 * @param {Object} options - Additional options for the query
 * @returns {Object} - Query result
 */
export const useItemEvaluations = (itemId, options = {}) => {
  return useQuery({
    queryKey: ['items', 'evaluations', itemId],
    queryFn: () => fetchItemEvaluations(itemId),
    enabled: !!itemId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

// Mutation Hooks

/**
 * Hook to create a new item/asset
 * @param {Object} options - Additional options for the mutation
 * @returns {Object} - Mutation result
 */
export const useCreateItem = (options = {}) => {
  return useMutation({
    mutationFn: createItem,
    ...options,
  });
};

/**
 * Hook to update an existing item/asset
 * @param {Object} options - Additional options for the mutation
 * @returns {Object} - Mutation result
 */
export const useUpdateItem = (options = {}) => {
  return useMutation({
    mutationFn: ({ itemId, itemData }) => updateItem(itemId, itemData),
    ...options,
  });
};

/**
 * Hook to delete an item/asset
 * @param {Object} options - Additional options for the mutation
 * @returns {Object} - Mutation result
 */
export const useDeleteItem = (options = {}) => {
  return useMutation({
    mutationFn: deleteItem,
    ...options,
  });
};

// Export individual fetch functions for direct use if needed
export {
  fetchAllItems,
  fetchItemById,
  fetchItemImages,
  fetchItemVariants,
  fetchItemEvaluations,
  createItem,
  updateItem,
  deleteItem,
};